import { SOURCE_TYPE_ID_SMARTSUITE } from "@smartsuite/automation-conditions";
import type { ActionReference } from "@smartsuite/automation-engine-api";
import { RecordListInputConfig_Reference } from "@smartsuite/automation-engine-api";
import type { ActionCandidate } from "../../../types/candidate";
import {
  ActionLabel,
  FIELD_INPUT_PREFIX,
  INPUT_APPLICATION_ID,
} from "../../../utils/constants";
import { ejectDependValues } from "./eject-depend-values";
import { ejectReferences } from "./filters";

/** The table, and the solution, a step now hands its records over from. */
export interface RecordListTable {
  applicationId: string;
  solutionId?: string;
}

/** Whether an action takes its records from this Find Records action. */
export const consumesFindRecords = (
  action: ActionCandidate,
  findInstanceId: number,
): boolean =>
  action.recordList?.recordList.oneofKind === "actionReference" &&
  action.recordList.recordList.actionReference.instanceId === findInstanceId;

/** Whether an action takes its records from the trigger. */
export const consumesTrigger = (action: ActionCandidate): boolean =>
  action.recordList?.recordList.oneofKind === "reference" &&
  action.recordList.recordList.reference ===
    RecordListInputConfig_Reference.FROM_TRIGGER;

/** An action that picks a document template from the table its records come from. */
export const isPdfAction = (action: ActionCandidate): boolean =>
  action.actionReference?.actionId === ActionLabel.generatePdf ||
  action.actionReference?.actionId === ActionLabel.docsautomator;

const alreadyOn = (
  action: ActionCandidate,
  table: RecordListTable,
): boolean => {
  const metadata = action.recordList?.metadata ?? {};
  return (
    metadata.applicationId === table.applicationId &&
    (table.solutionId === undefined || metadata.solutionId === table.solutionId)
  );
};

/**
 * The step an action takes its records from now works on another table: the
 * action follows it.
 *
 * The table and the solution are properties of the records, copied from the
 * source onto the action's record list when the source was picked
 * (BUG-22529). That copy is what the card, the field list and the engine read,
 * so it is rewritten here. What was configured against the old table goes:
 * the field inputs, since they name that table's fields, and the pills that
 * read the source's outputs, since those are that table's fields too. The
 * action then describes itself again, listing the new table's fields.
 *
 * A legacy action that still carries an `application-id` input, which the
 * editor used to keep in step with the source, has it rewritten as well. A
 * PDF action also loses the template it had picked, since a template belongs
 * to a table.
 *
 * Nothing happens when the action's list is already on that table, or when
 * the list is not one of SmartSuite records.
 */
export const followRecordListSource = (
  action: ActionCandidate,
  table: RecordListTable,
  source: { actionReference: ActionReference } | { trigger: true },
): ActionCandidate => {
  const recordList = action.recordList;
  if (recordList === undefined) {
    return action;
  }
  if (
    recordList.sourceTypeId !== undefined &&
    recordList.sourceTypeId !== SOURCE_TYPE_ID_SMARTSUITE
  ) {
    return action;
  }
  if (alreadyOn(action, table)) {
    return action;
  }

  const ejected =
    "trigger" in source
      ? ejectReferences(action, "triggerReference")
      : ejectReferences(action, "actionReference", source.actionReference);
  const followed: ActionCandidate = {
    ...ejected,
    recordList: {
      ...recordList,
      sourceTypeId: SOURCE_TYPE_ID_SMARTSUITE,
      metadata: {
        ...recordList.metadata,
        applicationId: table.applicationId,
        ...(table.solutionId === undefined
          ? {}
          : { solutionId: table.solutionId }),
      },
    },
    inputs: ejected.inputs.filter(
      (input) => !input.inputId.startsWith(FIELD_INPUT_PREFIX),
    ),
  };

  if (isPdfAction(action)) {
    followed.needToUpdateDynamicDescription = true;
    return ejectDependValues(followed, table.applicationId);
  }
  if (action.description.recordListInput?.dynamic === true) {
    followed.needToUpdateDynamicDescription = true;
  }
  return hasInput(followed, INPUT_APPLICATION_ID)
    ? ejectDependValues(followed, table.applicationId)
    : followed;
};

const hasInput = (action: ActionCandidate, inputId: string): boolean =>
  action.inputs.some((input) => input.inputId === inputId);
